<template>
  <transition
    enter-active-class="transition-opacity duration-200 ease-out"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="transition-opacity duration-150 ease-in"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div
      v-if="isOpen"
      class="fixed inset-0 flex items-center justify-center z-50 p-4"
    >
      <!-- 오버레이 (배경) -->
      <div
        class="fixed inset-0 bg-black/60 backdrop-blur-[2px] transition-opacity"
        @click="$emit('close')"
      ></div>

      <!-- 모달 본체 -->
      <div
        class="relative bg-white rounded-2xl shadow-2xl w-full max-w-[500px] overflow-hidden z-10 transform transition-all border border-gray-100"
        @keyup.enter="handleChangePassword"
      >
        <!-- 상단 헤더 -->
        <div
          class="bg-gradient-to-r from-slate-800 to-slate-900 px-6 py-4 flex items-center justify-between border-b border-slate-700/50"
        >
          <div class="flex items-center space-x-3 text-white">
            <div
              class="w-9 h-9 rounded-xl bg-blue-500/20 border border-blue-400/30 flex items-center justify-center text-blue-400 shadow-inner flex-shrink-0"
            >
              <i class="fa fa-key text-base"></i>
            </div>
            <div>
              <h3 class="font-bold text-base tracking-wide text-white leading-tight">
                비밀번호 변경
              </h3>
              <p class="text-xs text-slate-400 mt-0.5 font-normal">
                안전한 계정 관리를 위해 비밀번호를 변경해 주세요.
              </p>
            </div>
          </div>

          <!-- 닫기 버튼 -->
          <button
            type="button"
            @click="$emit('close')"
            class="w-8 h-8 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700/60 transition-colors flex items-center justify-center cursor-pointer"
            title="닫기"
          >
            <i class="fa fa-times text-base"></i>
          </button>
        </div>

        <!-- 본문 폼 영역 -->
        <div class="p-6 space-y-4">
          <!-- 안내 문구 박스 -->
          <div
            class="bg-blue-50/80 border border-blue-100 rounded-xl p-3.5 flex items-start space-x-3 text-xs text-blue-800 shadow-xs"
          >
            <i class="fa fa-shield-alt text-blue-600 text-sm mt-0.5 flex-shrink-0"></i>
            <div class="leading-relaxed">
              <strong class="font-semibold text-blue-900">현재 비밀번호</strong>를 입력하신 후 새로운 비밀번호를 설정해 주세요.
            </div>
          </div>

          <!-- 입력 필드 그룹 -->
          <div class="space-y-3 pt-1">
            <UCText
              label="현재 비밀번호"
              v-model="currentPassword"
              type="password"
              placeholder="현재 비밀번호를 입력하세요"
              widthLabel="130px"
            />

            <div class="space-y-1">
              <UCText
                label="새 비밀번호"
                v-model="newPassword"
                type="password"
                placeholder="새 비밀번호를 입력하세요"
                widthLabel="130px"
              />
              <p class="pl-[135px] text-[11px] text-gray-500 flex items-center space-x-1">
                <i class="fa fa-info-circle text-blue-500 text-[10px]"></i>
                <span>영문 대/소문자, 숫자, 특수문자 포함 8자 이상</span>
              </p>
            </div>

            <UCText
              label="새 비밀번호 확인"
              v-model="confirmPassword"
              type="password"
              placeholder="새 비밀번호를 다시 입력하세요"
              widthLabel="130px"
            />
          </div>

          <!-- 에러 메시지 알림 -->
          <div
            v-if="errorMessage"
            class="bg-red-50 border border-red-200/80 rounded-xl p-3 text-xs text-red-600 flex items-center space-x-2.5 shadow-xs"
          >
            <i class="fa fa-exclamation-circle text-red-500 text-sm flex-shrink-0"></i>
            <span class="font-medium">{{ errorMessage }}</span>
          </div>

          <!-- 하단 버튼 영역 -->
          <div class="flex items-center justify-end space-x-2.5 pt-4 border-t border-gray-100">
            <button
              type="button"
              @click="$emit('close')"
              class="px-4 py-2 text-xs font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 rounded-lg border border-gray-300 transition-colors shadow-xs flex items-center space-x-1.5 cursor-pointer"
            >
              <i class="fa fa-times text-xs text-gray-500"></i>
              <span>취소</span>
            </button>
            <button
              type="button"
              @click="handleChangePassword"
              class="px-5 py-2 text-xs font-bold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 active:from-blue-800 active:to-indigo-800 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 flex items-center space-x-1.5 cursor-pointer"
            >
              <i class="fa fa-lock text-xs text-white"></i>
              <span>비밀번호 변경</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, watch, defineProps, defineEmits } from "vue";
import { notify } from "@kyvg/vue3-notification";

import UCText from "@/components/UserControl/UCText.vue";

const props = defineProps({
  isOpen: Boolean,
});
const emit = defineEmits(["close"]);

const currentPassword = ref("");
const newPassword = ref("");
const confirmPassword = ref("");

// 팝업이 열릴 때 값 초기화
watch(
  () => props.isOpen,
  (newVal) => {
    if (newVal) {
      currentPassword.value = "";
      newPassword.value = "";
      confirmPassword.value = "";
      errorMessage.value = "";
    }
  }
);

const handleChangePassword = () => {
  if (validatePassword()) {
    console.log(
      `currentPassword[${currentPassword.value}] newPassword[${newPassword.value}] confirmPassword[${confirmPassword.value}]`
    );

    notify({
      title: "실패",
      text: "비밀번호 변경 API 호출이 필요합니다.",
      type: "error",
    });

    // 비밀번호 변경 API 호출

    // notify({
    //   title: "성공",
    //   text: "비밀번호 변경되었습니다.",
    //   type: "success",
    // });
  }
};

const errorMessage = ref("");
const validatePassword = () => {
  if (!currentPassword.value) {
    errorMessage.value = "현재 비밀번호를 입력해주세요.";
    return false;
  }

  const password = newPassword.value;

  // 최소 길이
  if (password.length < 8) {
    errorMessage.value = "비밀번호는 최소 8자 이상이어야 합니다.";
    return false;
  }

  // 대문자 포함 여부
  if (!/[A-Z]/.test(password)) {
    errorMessage.value =
      "비밀번호에는 대문자가 최소 1자 이상 포함되어야 합니다.";
    return false;
  }

  // 소문자 포함 여부
  if (!/[a-z]/.test(password)) {
    errorMessage.value =
      "비밀번호에는 소문자가 최소 1자 이상 포함되어야 합니다.";
    return false;
  }

  // 숫자 포함 여부
  if (!/[0-9]/.test(password)) {
    errorMessage.value = "비밀번호에는 숫자가 최소 1자 이상 포함되어야 합니다.";
    return false;
  }

  // 특수문자 포함 여부 (선택)
  if (!/[!@#$%^&*]/.test(password)) {
    errorMessage.value = "비밀번호에는 특수문자가 포함되어야 합니다.";
    return false;
  }

  // 비밀번호 확인 일치 여부
  if (newPassword.value !== confirmPassword.value) {
    errorMessage.value = "새 비밀번호가 일치하지 않습니다.";
    return false;
  }

  errorMessage.value = "";
  return true;
};
</script>
